import { useState, useEffect, useMemo } from 'react';
import { Paper, MenuItem, Box, ClickAwayListener } from '@mui/material';
import StyledTextField from '@shared/components/StyledTextField';

const normalize = text =>
  text?.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase() || '';

const AirportAutoComplete = ({
  value,
  onChange,
  placeHolder = 'Ciudad',
  skipOpenRef,
  airports,
  sectionNumber,
}) => {
  const [inputValue, setInputValue] = useState(value?.label || '');
  const [open, setOpen] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // Actualizamos inputValue si cambia el value externo
  useEffect(() => {
    const labelSinIata = value?.label?.replace(/\s*\([^)]*\)\s*$/, '') || '';
    setInputValue(labelSinIata ? `${value.iata_code} - ${labelSinIata}` : '');
  }, [value]);

  // Filtrado optimizado con useMemo
  const airportsToShow = useMemo(() => {
    if (inputValue.length < 3) return [];
    const normalizedInput = normalize(inputValue);
    return airports.filter(
      a =>
        normalize(a.label).includes(normalizedInput) ||
        normalize(a.iata_code) === normalizedInput
    );
  }, [inputValue, airports]);

  // Abrir el menú solo si el usuario escribió y hay resultados
  useEffect(() => {
    if (!skipOpenRef?.current && userInteracted) {
      setOpen(airportsToShow.length > 0);
    } else {
      setOpen(false);
    }
  }, [airportsToShow, userInteracted, skipOpenRef]);

  const handleSelect = airport => {
    setInputValue(airport.label);
    onChange(airport);
    setOpen(false);
    setUserInteracted(false);
    if (placeHolder === 'Destino')
      try {
        if (sectionNumber === 0 || sectionNumber) {
          try {
            window.mopc[sectionNumber]();
          } catch (error) {
            console.log('InvalidSection');
          }
        } else {
          if (window.opc) window.opc();
        }
      } catch (error) {
        console.error('AirportAutoComplete handleSelect error', error);
      }
  };

  const handleInputChange = e => {
    const newValue = e.target.value;
    if (inputValue.length > newValue.length) {
      onChange(null);
    }
    setInputValue(newValue);
    setUserInteracted(true);
  };

  const handleClear = () => {
    setInputValue('');
    setUserInteracted(false);
    setOpen(false);
    onChange(null);
  };

  const handleKeyDown = event => {
    if (
      (event.key === 'Tab' || event.key === 'Enter') &&
      inputValue.length >= 3
    ) {
      if (
        inputValue.includes('-') &&
        inputValue.includes('(') &&
        inputValue.includes(')')
      )
        return;

      let match = airportsToShow.find(
        a => a.iata_code.toUpperCase() === inputValue.toUpperCase()
      );
      if (!match && airportsToShow.length > 0) match = airportsToShow[0];

      if (match) handleSelect(match);

      if (event.key === 'Tab') setTimeout(() => setOpen(false), 0);
      else event.preventDefault();
    }
  };

  const handleClickAway = () => {
    setOpen(false);
    setUserInteracted(false);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <StyledTextField
        label={placeHolder}
        placeholder="Ingresá una ciudad"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        height="55px"
        minHeight="55px"
        showEndAdornment={!!inputValue}
        onEndAdornmentClick={handleClear}
      />

      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: 'max-content',
              maxHeight: 300,
              overflowY: 'auto',
              zIndex: 1300,
              mt: 0.5,
            }}
          >
            {airportsToShow.length > 0 ? (
              airportsToShow.map((airport, i) => (
                <MenuItem
                  key={airport.id ?? i}
                  onClick={() => handleSelect(airport)}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    paddingY: 1,
                    paddingX: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    height: '50px',
                    width: 'max-content',
                  }}
                >
                  {airport.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled sx={{ fontSize: '14px' }}>
                No se encontraron resultados
              </MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default AirportAutoComplete;
